import { SetStateAction, useCallback, useEffect, useState } from "react"

type ScrollTriggerOptions = {
    onTopReached?: () => void
    topStopConition?: () => boolean
    
    onBottomReached?: () => void
    bottomStopCondition?: () => boolean
    
    dependencies?: any[]
    scrollParent?: HTMLDivElement | null
}

export const useScrollTrigger: (opt: ScrollTriggerOptions) => [HTMLDivElement | null, React.Dispatch<SetStateAction<HTMLDivElement | null>>] = ({
    onTopReached,
    topStopConition,
    onBottomReached,
    bottomStopCondition,
    dependencies,
    scrollParent,
}) => {
    const [ref, setRef] = useState<HTMLDivElement | null>(null)

    const detectBottomReached = useCallback((): boolean => {
        if (!ref) {
            return false
        }

        const offset = ref.getBoundingClientRect()

        const criteria = scrollParent
            ? scrollParent.scrollHeight === scrollParent.scrollTop + scrollParent.clientHeight
            : offset.bottom >= (window.innerHeight || document.documentElement.clientHeight)

        if (criteria) {
            return true
        }

        return false
    }, [ref, scrollParent])

    const detectTopReached = useCallback((): boolean => {
        if (!ref) {
            return false
        }

        const offset = ref.getBoundingClientRect()

        const criteria = scrollParent
            ? offset.top >= scrollParent.getBoundingClientRect().top
            : offset.top <= 0

        if (criteria) {
            return true
        }

        return false
    }, [ref, scrollParent])

    const callback = useCallback(() => {
        if (detectBottomReached() && (!bottomStopCondition || !bottomStopCondition!())) {
            onBottomReached && onBottomReached()
        }

        if (detectTopReached() && (!topStopConition || !topStopConition!())) {
            onTopReached && onTopReached()
        }
    }, [detectBottomReached, detectTopReached, topStopConition, bottomStopCondition, onBottomReached, onTopReached])

    useEffect(() => {
        if (scrollParent) {
            scrollParent.addEventListener("scroll", callback, true)
        } else {
            window.addEventListener("scroll", callback, true)
        }

        return () => {
            if (scrollParent) {
                scrollParent.removeEventListener("scroll", callback, true)
            } else {
                window.removeEventListener("scroll", callback, true)
            }
        }
    }, [ref, scrollParent, callback, onBottomReached, bottomStopCondition, ...(dependencies || [])])

    return [ref, setRef]
}